import { useCallback, useEffect } from "react";
import type { AppViewMode, ReturnTo } from "../domain/contracts";

type NavigationArgs = {
  returnTo: ReturnTo;
  setReturnTo: (value: ReturnTo) => void;
  setViewRef: (viewRef: AppViewMode | null) => void;
  selectVillage: (villageRef: string | null) => void;
  selectInterviewee: (intervieweeRef: string) => void;
  clearInterviewee: () => void;
};

export function useAppNavigation({
  returnTo,
  setReturnTo,
  setViewRef,
  selectVillage,
  selectInterviewee,
  clearInterviewee,
}: NavigationArgs) {
  const scrollToMap = useCallback(() => {
    const attemptScroll = () => {
      const target = document.getElementById("map");
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }

      setTimeout(() => {
        const retryTarget = document.getElementById("map");
        retryTarget?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 120);
    };

    window.requestAnimationFrame(attemptScroll);
  }, []);
  // On popstate (browser back/forward), derive returnTo from the new URL's view param.
  // We can't know the exact parent, so we default to "map".
  // This is called BEFORE the URL-driven viewMode updates, so we set a reasonable fallback.
  useEffect(() => {
    function handlePopState() {
      // When pressing browser back/forward, we can't reliably know the parent view.
      // Default returnTo to "map" so the UI back button goes to map.
      setReturnTo("map");
    }
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [setReturnTo]);

  const handleViewFullProfile = useCallback(
    (villageRef: string) => {
      selectVillage(villageRef);
      setReturnTo(returnTo);
      setViewRef("village");
    },
    [selectVillage, setReturnTo, setViewRef, returnTo],
  );

  const handleSelectIntervieweeFromVillage = useCallback(
    (intervieweeRef: string) => {
      selectInterviewee(intervieweeRef);
      setReturnTo("village");
      setViewRef("interviewee");
    },
    [selectInterviewee, setReturnTo, setViewRef],
  );

  const handleSelectIntervieweeFromList = useCallback(
    (intervieweeRef: string, villageId: string) => {
      selectVillage(villageId);
      selectInterviewee(intervieweeRef);
      setReturnTo("intervieweesList");
      setViewRef("interviewee");
    },
    [selectVillage, selectInterviewee, setReturnTo, setViewRef],
  );

  const handleSelectVillageFromList = useCallback(
    (villageRef: string) => {
      selectVillage(villageRef);
      setReturnTo("villagesList");
      setViewRef("village");
    },
    [selectVillage, setReturnTo, setViewRef],
  );

  const handleBackToList = useCallback(() => {
    clearInterviewee();
    // Navigate to the parent view based on returnTo
    if (returnTo === "villagesList" || returnTo === "intervieweesList") {
      setViewRef(returnTo);
      setReturnTo("map");
    } else if (returnTo === "village") {
      setViewRef("village");
      setReturnTo("map");
    } else {
      setViewRef("map");
      setReturnTo("map");
      scrollToMap();
    }
  }, [returnTo, setViewRef, setReturnTo, clearInterviewee, scrollToMap]);

  const handleHeaderNav = useCallback(
    (target: "home" | "villages" | "interviewees") => {
      if (target === "home") {
        setViewRef("map");
      } else if (target === "villages") {
        setViewRef("villagesList");
      } else {
        setViewRef("intervieweesList");
      }
      setReturnTo("map");
    },
    [setViewRef, setReturnTo],
  );

  return {
    handleViewFullProfile,
    handleSelectIntervieweeFromVillage,
    handleSelectIntervieweeFromList,
    handleSelectVillageFromList,
    handleBackToList,
    handleHeaderNav,
  };
}
